import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  FilePenLine,
  Download,
  Trash2,
} from "lucide-react";
import constant from "../../constant";
import { Helmet } from "react-helmet";
import UsersContent from "../users/userContent";
import DevicesContent from "../devices/deviceContent";
import PatientsContent from "../onboarded/patientContent";
import DoctorsContent from "../doctors/doctorContent";
import IssuesContent from "../issues/issueContent";
import MedicinesTiming from "../medicinesTiming";
import HospitalForm from "../ui/hospitalForm";

const HospitalDetails = ({ title }) => {
  const { hospitalId } = useParams();
  const navigate = useNavigate();
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("users"); // Default tab
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);

  const userRole = localStorage.getItem("role");
  const adminSelectedUser = localStorage.getItem("adminSelectedUser");

  useEffect(() => {
    const fetchHospitalDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found");
          navigate("/login");
          return;
        }

        const response = await fetch(
          `${constant.REACT_APP_API_URL}/hospital/${hospitalId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch hospital details");
        }

        const result = await response.json();
        setHospital(result);
      } catch (error) {
        setError(error.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchHospitalDetails();
  }, [hospitalId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 px-6 pt-16 md:pt-8">
        <div className="max-w-8xl mx-auto px-0 lg:px-8 py-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-500 hover:text-gray-700 transition-colors mr-1"
                aria-label="Back to Hospitals"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <h2 className="text-md font-bold text-gray-800 uppercase">
                {hospital?.name || "Hospital Details"}
              </h2>
            </div>

            {userRole === 'admin' ? (
              ''
            ) : (
              <button
                onClick={() => navigate(`/hospitals/edit/${hospitalId}`)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-150"
              >
                <FilePenLine size={18} className="mr-2" />
                Edit
              </button>
            )}
          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex flex-wrap -mb-px">
              {[
                { key: "users", label: "Users" },
                { key: "devices", label: "Devices" },
                { key: "patients", label: "Patients" },
                { key: "hospitalDetails", label: "Hospital Details" },
                { key: "doctors", label: "Doctors" },
                { key: "issues", label: "Issues" },
                { key: "medicinesTiming", label: "Medicines Timing" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap px-6 py-4 text-sm font-medium mr-2 mb-0 border-b-2 ${activeTab === tab.key
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  aria-current={activeTab === tab.key ? "page" : undefined}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          {/* Tab Content */}
          <div>
            {/* Users Tab */}
            <div className={`${activeTab === "users" ? "block" : "hidden"}`}>
              <div className="">
                <UsersContent
                />
              </div>
            </div>

            {/* Devices Tab */}
            <div className={`${activeTab === "devices" ? "block" : "hidden"}`}>
              <div className="">
                <DevicesContent />
              </div>
            </div>

            {/* Patients Tab */}
            <div className={`${activeTab === "patients" ? "block" : "hidden"}`}>
              <div className="">
                <PatientsContent />
              </div>
            </div>

            {/* Hospital Details Tab */}
            <div className={`${activeTab === "hospitalDetails" ? "block" : "hidden"}`}>
              <div className="">
                <div className="bg-gray-50 p-6 rounded-lg space-y-6">

                <div>
            <h1 className="text-2xl font-bold text-gray-900">Hospital Details</h1>
          </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hospital Name
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.name}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hospital Id
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.hospital_id}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.state}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.city}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.street}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pincode
                      </label>
                      <p className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100">
                        {hospital.pincode}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Doctors Tab */}
            <div className={`${activeTab === "doctors" ? "block" : "hidden"}`}>
              <div className="">
                <DoctorsContent
                />
              </div>
            </div>

            {/* Issues Tab */}
            <div className={`${activeTab === "issues" ? "block" : "hidden"}`}>
              <div className="">
                <IssuesContent
                />
              </div>
            </div>

                        {/* Medicine Timings Tab */}
                        <div className={`${activeTab === "medicinesTiming" ? "block" : "hidden"}`}>
              <div className="">
                <MedicinesTiming
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default HospitalDetails;